
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import UserService from "../../services/user.service";

// Get officers
export const getOfficers = createAsyncThunk(
    "user/getOfficers",
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await UserService.getOfficers(params);

            if (response?.status !== 1) {
                return rejectWithValue(
                    response?.message || "Failed to fetch officers"
                );
            }

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch officers"
            );
        }
    }
);

// Create officer
export const createOfficer = createAsyncThunk(
    "user/createOfficer",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await UserService.createOfficer(payload);

            if (response?.status !== 1) {
                return rejectWithValue(
                    response?.message || "Failed to create officer"
                );
            }

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to create officer"
            );
        }
    }
);

// Bulk create officers
export const bulkCreateOfficer = createAsyncThunk(
    "user/bulkCreateOfficer",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await UserService.bulkCreateOfficer(payload);

            if (response?.status !== 1) {
                return rejectWithValue(
                    response?.message || "Failed to bulk create officers"
                );
            }

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to bulk create officers"
            );
        }
    }
);

// Update officer
export const updateOfficer = createAsyncThunk(
    "user/updateOfficer",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const response = await UserService.updateOfficer(id, payload);

            if (response?.status !== 1) {
                return rejectWithValue(
                    response?.message || "Failed to update officer"
                );
            }

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to update officer"
            );
        }
    }
);

// Delete officer
export const deleteOfficer = createAsyncThunk(
    "user/deleteOfficer",
    async (id, { rejectWithValue }) => {
        try {
            const response = await UserService.deleteOfficer(id);

            if (response?.status !== 1) {
                return rejectWithValue(
                    response?.message || "Failed to delete officer"
                );
            }

            return { id, data: response.data };
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to delete officer"
            );
        }
    }
);

// Synchronize officer assignments
export const syncOfficerAssignments = createAsyncThunk(
    "user/syncOfficerAssignments",
    async (payload = {}, { rejectWithValue }) => {
        try {
            const response = await UserService.syncOfficerAssignments(payload);

            if (response?.status !== 1) {
                return rejectWithValue(
                    response?.message || "Failed to synchronize assignments"
                );
            }

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to synchronize assignments"
            );
        }
    }
);

const initialState = {
    officers: [],
    pagination: {
        total: 0,
        currentPage: 1,
        totalPages: 0,
    },

    loading: false,
    createLoading: false,
    bulkCreateLoading: false,
    updateLoading: false,
    deleteLoading: false,
    syncLoading: false,

    error: null,
    successMessage: null,
};

const userSlice = createSlice({
    name: "user",
    initialState,

    reducers: {
        clearUserError: (state) => {
            state.error = null;
        },

        clearUserMessage: (state) => {
            state.successMessage = null;
        },

        resetUserState: () => initialState,
    },

    extraReducers: (builder) => {
        builder

            // Get officers
            .addCase(getOfficers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getOfficers.fulfilled, (state, action) => {
                state.loading = false;

                state.officers = action.payload?.officers || [];

                state.pagination = {
                    total: action.payload?.total || 0,
                    currentPage: action.payload?.currentPage || 1,
                    totalPages: action.payload?.totalPages || 0,
                };
            })
            .addCase(getOfficers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error.message;
            })

            // Create officer
            .addCase(createOfficer.pending, (state) => {
                state.createLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(createOfficer.fulfilled, (state, action) => {
                state.createLoading = false;
                state.successMessage = "Officer created successfully";

                const officer =
                    action.payload?.officer || action.payload;

                if (officer?._id) {
                    state.officers.unshift(officer);
                    state.pagination.total += 1;
                }
            })
            .addCase(createOfficer.rejected, (state, action) => {
                state.createLoading = false;
                state.error = action.payload || action.error.message;
            })

            // Bulk create officers
            .addCase(bulkCreateOfficer.pending, (state) => {
                state.bulkCreateLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(bulkCreateOfficer.fulfilled, (state, action) => {
                state.bulkCreateLoading = false;
                state.successMessage =
                    "Officers imported successfully";
            })
            .addCase(bulkCreateOfficer.rejected, (state, action) => {
                state.bulkCreateLoading = false;
                state.error = action.payload || action.error.message;
            })

            // Update officer
            .addCase(updateOfficer.pending, (state) => {
                state.updateLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(updateOfficer.fulfilled, (state, action) => {
                state.updateLoading = false;
                state.successMessage = "Officer updated successfully";

                const officer =
                    action.payload?.officer || action.payload;

                if (officer?._id) {
                    const index = state.officers.findIndex(
                        (item) => item._id === officer._id
                    );

                    if (index !== -1) {
                        state.officers[index] = officer;
                    }
                }
            })
            .addCase(updateOfficer.rejected, (state, action) => {
                state.updateLoading = false;
                state.error = action.payload || action.error.message;
            })

            // Delete officer
            .addCase(deleteOfficer.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(deleteOfficer.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.successMessage = "Officer deleted successfully";

                state.officers = state.officers.filter(
                    (item) => item._id !== action.payload.id
                );

                state.pagination.total = Math.max(
                    0,
                    state.pagination.total - 1
                );
            })
            .addCase(deleteOfficer.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload || action.error.message;
            })

            // Sync assignments
            .addCase(syncOfficerAssignments.pending, (state) => {
                state.syncLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(syncOfficerAssignments.fulfilled, (state) => {
                state.syncLoading = false;
                state.successMessage =
                    "Officer assignments synchronized successfully";
            })
            .addCase(syncOfficerAssignments.rejected, (state, action) => {
                state.syncLoading = false;
                state.error = action.payload || action.error.message;
            });
    },
});

export const {
    clearUserError,
    clearUserMessage,
    resetUserState,
} = userSlice.actions;

export default userSlice.reducer;
